/**
 * EscrowSetup.jsx — Escrow milestone setup for a deal.
 *
 * Milestone form: 1–10 milestones with title, deliverables, acceptance criteria,
 *                 deadline (weeks), amount.
 * Client-side validation: sum of amounts must equal deal value.
 * Handles: MILESTONE_SUM_MISMATCH, MILESTONE_COUNT_INVALID.
 *
 * Requirements: 23.5
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../../utils/auth';

const EMPTY_MILESTONE = () => ({
  title: '',
  deliverables: '',
  acceptance_criteria: '',
  deadline_weeks: '',
  amount: '',
});
const DEADLINE_WEEKS = Array.from({ length: 20 }, (_, i) => i + 1);

const ERROR_MESSAGES = {
  MILESTONE_SUM_MISMATCH: 'The sum of milestone amounts must equal the deal value.',
  MILESTONE_COUNT_INVALID: 'You must have between 1 and 10 milestones.',
  ESCROW_NOT_AVAILABLE: 'Escrow is not available for this deal.',
  ESCROW_ALREADY_EXISTS: 'Escrow has already been set up for this deal.',
  CONTRACT_NOT_COMPLETED:
    'Escrow cannot be set up until the contract has been signed by both parties and approved by admin.',
};

export default function EscrowSetup() {
  const { id: dealId } = useParams();
  const navigate = useNavigate();

  const [deal, setDeal] = useState(null);
  const [milestones, setMilestones] = useState([EMPTY_MILESTONE()]);
  const [apiError, setApiError] = useState('');
  const [sumError, setSumError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/api/v1/deals/${dealId}/`)
      .then(({ data }) => setDeal(data))
      .catch(() => setApiError('Failed to load deal details.'));
  }, [dealId]);

  const handleMilestoneChange = (index, field, value) => {
    setMilestones((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setSumError('');
    setApiError('');
  };

  const addMilestone = () => {
    if (milestones.length < 10) {
      setMilestones((prev) => [...prev, EMPTY_MILESTONE()]);
    }
  };

  const removeMilestone = (index) => {
    if (milestones.length > 1) {
      setMilestones((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const validateSum = () => {
    if (!deal) return false;
    const total = milestones.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
    const dealValue = Number(deal.deal_value);
    if (Math.abs(total - dealValue) > 0.01) {
      setSumError(
        `Sum of milestone amounts (Rs.${total.toLocaleString('en-IN')}) must equal deal value (Rs.${dealValue.toLocaleString('en-IN')}).`
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateSum()) return;

    setLoading(true);
    setApiError('');

    const today = new Date();
    const toISODate = (weeks) => {
      const dt = new Date(today);
      dt.setDate(dt.getDate() + Number(weeks) * 7);
      return dt.toISOString().slice(0, 10);
    };
    const payload = {
      deal_id: Number(dealId),
      milestones: milestones.map((m, i) => ({
        sequence_order: i + 1,
        title: m.title,
        deliverables: m.deliverables,
        acceptance_criteria: m.acceptance_criteria,
        deadline: toISODate(m.deadline_weeks),
        amount: m.amount,
      })),
    };

    try {
      await apiClient.post('/api/v1/escrow/', payload);
      setSubmitted(true);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      setApiError(ERROR_MESSAGES[code] || 'Failed to set up escrow. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, p: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Escrow Set Up
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Escrow has been configured successfully with {milestones.length} milestone(s).
        </Typography>
        <Button variant="outlined" onClick={() => navigate(`/deals/${dealId}`)}>
          Back to Deal
        </Button>
      </Box>
    );
  }

  if (deal && deal.escrow_id) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, p: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Escrow is already set up for this deal (reference #{deal.escrow_id}). Use{' '}
          <strong>Back to deal</strong> to continue.
        </Alert>
        <Button variant="outlined" onClick={() => navigate(`/deals/${dealId}`)}>
          Back to deal
        </Button>
      </Box>
    );
  }

  if (deal && deal.contract_status !== 'COMPLETED') {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Escrow cannot be set up until the contract has been signed by both parties and approved by admin.
          {deal.contract_status ? ` Contract status: ${deal.contract_status}` : ' No contract initiated yet.'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(`/deals/${dealId}`)}>
          Back to deal
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 720, mx: 'auto', mt: 4, p: 3 }}
      noValidate
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/deals/${dealId}`)}
        sx={{
          mb: 3,
          borderColor: 'rgba(99, 102, 241, 0.4)',
          color: '#818cf8',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
          }
        }}
      >
        Back to Deal
      </Button>
      <Typography variant="h4" gutterBottom>
        Set Up Escrow
      </Typography>
      {deal && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Deal #{dealId} — Total Value: Rs.{Number(deal.deal_value).toLocaleString('en-IN')}
        </Typography>
      )}

      {milestones.map((ms, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2 }} elevation={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1">Milestone {index + 1}</Typography>
            {milestones.length > 1 && (
              <IconButton
                size="small"
                onClick={() => removeMilestone(index)}
                aria-label={`Remove milestone ${index + 1}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />

          <TextField
            label="Title"
            value={ms.title}
            onChange={(e) => handleMilestoneChange(index, 'title', e.target.value)}
            fullWidth
            required
            margin="dense"
            inputProps={{ 'aria-label': `Milestone ${index + 1} title` }}
          />
          <TextField
            label="Deliverables"
            value={ms.deliverables}
            onChange={(e) => handleMilestoneChange(index, 'deliverables', e.target.value)}
            fullWidth
            required
            multiline
            rows={2}
            margin="dense"
            inputProps={{ 'aria-label': `Milestone ${index + 1} deliverables` }}
          />
          <TextField
            label="Acceptance Criteria"
            value={ms.acceptance_criteria}
            onChange={(e) => handleMilestoneChange(index, 'acceptance_criteria', e.target.value)}
            fullWidth
            required
            multiline
            rows={2}
            margin="dense"
            inputProps={{ 'aria-label': `Milestone ${index + 1} acceptance criteria` }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Deadline (Weeks)"
              select
              value={ms.deadline_weeks}
              onChange={(e) => handleMilestoneChange(index, 'deadline_weeks', e.target.value)}
              fullWidth
              required
              margin="dense"
              inputProps={{ 'aria-label': `Milestone ${index + 1} deadline in weeks` }}
            >
              {DEADLINE_WEEKS.map((w) => (
                <MenuItem key={w} value={w}>
                  {w} week{w > 1 ? 's' : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount (Rs.)"
              type="number"
              value={ms.amount}
              onChange={(e) => handleMilestoneChange(index, 'amount', e.target.value)}
              fullWidth
              required
              margin="dense"
              inputProps={{ min: 1, 'aria-label': `Milestone ${index + 1} amount` }}
            />
          </Box>
        </Paper>
      ))}

      {milestones.length < 10 && (
        <Button
          startIcon={<AddIcon />}
          onClick={addMilestone}
          variant="outlined"
          sx={{ mb: 2 }}
          aria-label="Add milestone"
        >
          Add Milestone
        </Button>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        aria-label="Submit escrow setup"
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Set Up Escrow'}
      </Button>
      {apiError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {apiError}
        </Alert>
      )}
      {sumError && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {sumError}
        </Alert>
      )}
    </Box>
  );
}
